import React, { useState, useEffect } from "react";

const useAnimationFrame = callback => {
  // Use useRef for mutable variables that we want to persist
  // without triggering a re-render on their change
  const requestRef = React.useRef();
  const previousTimeRef = React.useRef();
  const initTimeRef = React.useRef(performance.now());
  
  const animate = time => {
    if (previousTimeRef.current != undefined) {
      const deltaTime = time - previousTimeRef.current;
      const clock = time - initTimeRef.current;
      callback({elapsedTime:clock/1000,delta:deltaTime})
    }
    previousTimeRef.current = time;
    requestRef.current = requestAnimationFrame(animate);
  }
  
  React.useEffect(() => {
    requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef.current);
  }, []); // Make sure the effect runs only once
}

function useWindowSize() {
  // Initialize state with undefined width/height so server and client renders match
  // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });
  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      // Set window width/height to state
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    // Add event listener
    window.addEventListener("resize", handleResize);
    // Call handler right away so state gets updated with initial window size
    handleResize();
    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []); // Empty array ensures that effect is only run on mount
  return windowSize;
}


function clamp(t,low,high) {
  return Math.max(Math.min(t, high), low)
}

function clampFrac(t,low,high) {
  return (clamp(t,low,high) - low) / (high-low)
}

function lerp(a, b, t){
  return a + (b-a) * t
}

function easeInQuad(x) {
  return x * x;
}

function easeOutQuad(x) {
  return 1 - (1 - x) * (1 - x);
}

function easeInOutQuad(x) {
  return x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;
}

function easeInOutQuint(x) {
  return x < 0.5 ? 16 * x * x * x * x * x : 1 - Math.pow(-2 * x + 2, 5) / 2;
}

function fx(t,low,high) {
  return easeInOutQuint(clampFrac(t,low,high))
}

function pinPositionText (top, start, release){
  return Math.max(0,pinPosition(top, start, release))
}

function pinPosition (top, start, release, D) {
  const d = D || 300
  if(top > start){
    if(top < release){
      return 0
    } else {
      return release-top > -d ? -d*easeInQuad(clampFrac(Math.abs(release-top),0,d)) : release-top
    }
  }
  return start-top < d ? d*easeInQuad(clampFrac(start-top,0,d)) : start-top
}

export {
  useAnimationFrame,
  useWindowSize,
  clamp,
  clampFrac,
  lerp,
  easeInQuad,
  easeOutQuad,
  easeInOutQuad,
  easeInOutQuint,
  fx,
  pinPosition,
  pinPositionText
}